import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ROLE_SCOPES, type ApiTokenCreated, type Scope } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { SCOPE_LABELS, useCan } from "../../auth/roles";
import { OrgLayout } from "../../components/Shell";
import { CopyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { formatDateTime, formatRelative } from "../../lib/format";

/** Scopes in the order the dialog lists them; a token may hold only scopes its creator's role has. */
export const TOKEN_SCOPES: readonly Scope[] = ["read", "write", "ai", "live", "tokens", "members", "billing", "audit", "admin"];

export function TokensPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const grantable = TOKEN_SCOPES.filter((sc) => can.role && ROLE_SCOPES[can.role].includes(sc));
  const tokens = useQuery({ queryKey: queryKeys.tokens(orgId), queryFn: () => api.orgs.tokens(orgId) });
  const projects = useQuery({ queryKey: queryKeys.projects(orgId), queryFn: () => api.orgs.projects(orgId) });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["read"]);
  const [projectId, setProjectId] = useState<string>("");
  const [expires, setExpires] = useState<string>("");
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null);

  const create = useMutation({
    mutationFn: () => api.orgs.createToken(orgId, { name, scopes, projectId: projectId || undefined, expiresAt: expires ? new Date(expires).toISOString() : undefined }),
    onSuccess: (t) => {
      setOpen(false);
      setCreated(t);
      setName("");
      void qc.invalidateQueries({ queryKey: queryKeys.tokens(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.orgs.deleteToken(orgId, id),
    onSuccess: () => {
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: queryKeys.tokens(orgId) });
      toast.push("info", "Token revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="API tokens" actions={<button className="btn primary" onClick={() => setOpen(true)}>Create token</button>} />
      <div className="card stack-table">
        <DataTable
          ariaLabel="API tokens"
          rows={tokens.data ?? []}
          loading={tokens.isPending}
          rowKey={(t) => t.id}
          empty="No tokens yet."
          columns={[
            { key: "name", header: "Name", label: "Name", render: (t) => (<><strong>{t.name}</strong>{t.prefix ? <div className="muted small mono">{t.prefix}…</div> : null}</>) },
            { key: "scopes", header: "Scopes", label: "Scopes", render: (t) => <span className="pill-row">{t.scopes.map((s) => <span key={s} className="badge neutral">{s}</span>)}</span> },
            { key: "proj", header: "Project", label: "Project", render: (t) => (t.projectId ? projects.data?.find((p) => p.id === t.projectId)?.name ?? t.projectId : <span className="muted">all</span>) },
            { key: "used", header: "Last used", label: "Last used", render: (t) => (t.lastUsedAt ? formatRelative(t.lastUsedAt) : <span className="muted">never</span>) },
            { key: "exp", header: "Expires", label: "Expires", render: (t) => (t.expiresAt ? formatDateTime(t.expiresAt) : <span className="muted">—</span>) },
            { key: "act", header: "", align: "right", render: (t) => (
              <RowMenu label={`Actions for ${t.name}`}>
                <button type="button" role="menuitem" className="danger" onClick={() => setRevoking({ id: t.id, name: t.name })}>Revoke</button>
              </RowMenu>
            ) },
          ]}
        />
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} title="Create API token" footer={<><button className="btn" onClick={() => setOpen(false)}>Cancel</button><button className="btn primary" onClick={() => create.mutate()} disabled={!name.trim() || scopes.length === 0 || create.isPending}>Create</button></>}>
        <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. CI source maps" autoFocus />}</Field>
        <Field label="Scopes" hint="A token can hold only the scopes your own role has.">
          {(p) => (
            <div id={p.id} style={{ display: "grid", gap: 6 }}>
              {grantable.map((sc) => (
                <label key={sc} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="checkbox" checked={scopes.includes(sc)} onChange={(e) => setScopes(e.target.checked ? [...scopes, sc] : scopes.filter((x) => x !== sc))} autoComplete="off" />
                  <span className="mono small">{sc}</span>
                  <span className="muted small">{SCOPE_LABELS[sc]}</span>
                </label>
              ))}
            </div>
          )}
        </Field>
        <div className="field-row">
          <Field label="Project" hint="Leave empty for all projects.">
            {(p) => (
              <select {...p} className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">All projects</option>
                {(projects.data ?? []).map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Expires" hint="Optional.">{(p) => <input {...p} className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />}</Field>
        </div>
      </Dialog>

      <Dialog open={!!created} onClose={() => setCreated(null)} title="Token created" footer={<button className="btn primary" onClick={() => setCreated(null)}>Done</button>}>
        <p className="muted">Copy it now. For security we cannot show it again.</p>
        <div className="mono-box" data-testid="token-secret">{created?.secret}</div>
        <div style={{ marginTop: 8 }}>
          <CopyButton value={created?.secret ?? ""} label="Copy token" />
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke token"
        confirmLabel="Revoke token"
        busy={del.isPending}
        onClose={() => { if (!del.isPending) setRevoking(null); }}
        onConfirm={() => revoking && del.mutate(revoking.id)}
      >
        <p style={{ margin: 0 }}><strong>{revoking?.name}</strong> stops working immediately. Anything still using it will be refused.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
